import { createSlice, isAnyOf } from '@reduxjs/toolkit';

import { logDevMessage, getLocalStorageExpire } from 'utils/utils';
import * as chatActions from 'store/slices/chat/chatSlice';
import { catchAppError, startOver, handleNetworkChange } from '../../appActions';
import { UI_SLICE_NAME } from '../slicesNames';
import { executeSearch } from '../results/resultsThunks';
import configThunks from '../config/configThunks';
import * as uiConstants from './uiConstants';
import authThunks from '../auth/authThunks';

const initialState = {
  profileModal: {
    // type can be any value from VALID_PROFILE_MODALS
    type: null,
    // data can be any
    data: null,
  },

  modals: {
    // error modals
    appError: false,

    // mobile search modals
    updateRegion: false,

    // misc modals
    highQuality: false,
    providersOfDistinction: false,
    mercyHealthRedirect: false,
    chat: false,
    filter: false,
    ClientConfigDevTools: false,
  },

  // other ui state
  showSplashPage: true,
  showCookieBanner: false,
  isCssEnabled: true,
  returnFocusToKey: null,
  feedbackPrompted: Boolean(getLocalStorageExpire('feedbackPrompted')),

  breakpoint: 'xs',
  showResultsMap: false,
  userHasMetOutOfPocketMaximum: false,
};

const uiSlice = createSlice({
  name: UI_SLICE_NAME,
  initialState,
  reducers: {
    openModal(state, action) {
      const modalName = action.payload;

      // loop through all modals and open the matching one. Set all others to false, ensuring that multiple modals are not opened at once
      for (const modalKey in state.modals) {
        if (modalKey === modalName) {
          state.modals[modalKey] = true;
        } else {
          state.modals[modalKey] = false;
        }
      }
    },
    closeModal(state, action) {
      const modalToClose = action.payload;

      if (!modalToClose) {
        // if no modal key is passed, close all modals
        for (const modalKey in state.modals) {
          if (state.modals[modalKey] !== undefined) state.modals[modalKey] = false;
        }
        // also reset the profile modal
        state.profileModal = initialState.profileModal;
      } else if (state.modals[modalToClose] !== undefined) {
        state.modals[modalToClose] = false;
      } else {
        logDevMessage(`No modal with key ${modalToClose}`);
      }
    },
    showCookieBanner(state) {
      state.showCookieBanner = true;
    },
    toggleUserHasMetOutOfPocketMaximum(state) {
      state.userHasMetOutOfPocketMaximum = !state.userHasMetOutOfPocketMaximum;
    },
    hideCookieBanner(state) {
      state.showCookieBanner = false;
    },
    setIsCssEnabled(state, action) {
      state.isCssEnabled = action.payload;
    },
    setReturnFocusToKey(state, action) {
      state.returnFocusToKey = action.payload;
    },
    closeFeedbackModal(state) {
      state.feedbackPrompted = true;
      state.profileModal = initialState.profileModal;
    },
    closeProfileModal(state, action) {
      const { promptFeedback } = action.payload || {};
      const feedbackModalAlreadyShown = state.feedbackPrompted;
      if (promptFeedback && !feedbackModalAlreadyShown) {
        state.profileModal = { type: 'feedback' };
      } else {
        state.profileModal = initialState.profileModal;
      }
    },
    openProfileModal(state, action) {
      let type;
      let data = null;

      if (typeof action.payload === 'string') {
        // in this case someone has passed dispatch(actions.ui.openProfileModal('feedback'))
        // meaning that no data was required
        type = action.payload;
      } else {
        // in this case someone has passed
        // dispatch(actions.ui.openProfileModal({ type: 'call', data: [] }))
        type = action.payload?.type;
        data = action.payload?.data;
      }

      if (uiConstants.VALID_PROFILE_MODALS.includes(type)) {
        state.profileModal.type = type;
        state.profileModal.data = data;
      } else {
        logDevMessage(`Invalid profile modal type: ${type}`);
      }
    },
    openShareModal(state, action) {
      const { results, location } = action.payload;

      // validate payload
      if (!Array.isArray(results) || !results.length) throw new Error('Invalid results in payload');
      if (!location?.coords) throw new Error('Invalid location in payload');

      state.profileModal.type = uiConstants.PROFILE_MODAL_TYPES.SHARE;
      state.profileModal.data = { results, location };
    },
    breakpointChanged(state, action) {
      state.breakpoint = action.payload;
    },
    toggleShowResultsMap(state) {
      state.showResultsMap = !state.showResultsMap;
    },
    setShowResultsMap(state, action) {
      const showMap = action.payload;
      state.showResultsMap = showMap;
    },
  },
  extraReducers(builder) {
    builder.addCase(catchAppError, (state) => {
      state.modals.appError = true;
    });

    builder.addCase(startOver, (state) => {
      // this should handle all state resets that need to happen on a caught error, or when the logo is clicked
      state.modals.appError = false;
    });

    builder.addCase(handleNetworkChange, (state) => {
      state.modals.updateRegion = false;
    });

    builder.addCase(executeSearch.fulfilled, (state, action) => {
      if (uiConstants.lgUpBreakpoints.includes(state.breakpoint)) {
        // on desktop sized screens, automatically open the map when results come in
        const { response = {} } = action.payload;

        // only open the map if there is at least 1 returned result
        if (response.count) state.showResultsMap = true;
      }
    });

    builder.addCase(authThunks.fetchInit.fulfilled, (state) => {
      state.showSplashPage = false;
    });

    builder.addCase(configThunks.fetchClientConfig.fulfilled, (state, action) => {
      // for closed access instances, don't hide splash page until fetchInit thunk is complete
      if (action.payload.CLOSED_ACCESS) state.showSplashPage = true;
      // for open access instances, hide splash page when config is available
      else state.showSplashPage = false;
    });

    builder.addMatcher(
      isAnyOf(chatActions.specialtySearchInPg, chatActions.goToHomepage),
      (state) => {
        state.modals.chat = false;
      }
    );
  },
});

export default uiSlice;
export const {
  openModal,
  closeModal,
  showCookieBanner,
  hideCookieBanner,
  closeFeedbackModal,
  closeProfileModal,
  openProfileModal,
  openShareModal,
  setIsCssEnabled,
  setReturnFocusToKey,
  breakpointChanged,
  toggleShowResultsMap,
  setShowResultsMap,
  toggleUserHasMetOutOfPocketMaximum,
} = uiSlice.actions;
